import { create, muiDialogV5, register, useModal } from '@ebay/nice-modal-react'
import React from 'react'
import { Box, Stack, Tooltip, Typography, styled } from '@mui/material'
import { BigNumber } from 'ethers'
import { commify, formatEther } from 'ethers/lib/utils.js'
import { useNetwork } from 'wagmi'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'

import { Text1 } from '../Left/styleds'
import FeeButton from './FeeButton'
import Dialog from '@/components/common/Dialog'
import { SupportedTicker, TICKERS_MAP_BY_NAME } from '@/constants/token'
import NetworkSelect from '@/components/common/NetworkSelect'
import BtcSelect from '@/components/common/BtcSelect'
import useFee from '@/hooks/useFee'
import { CHAIN_INFO } from '@/constants'

export const FeeModalKey = 'brc20-Fee-dialog'
const TextTitle = styled(Typography)`
  font-weight: 700;
  font-size: 40px;
  line-height: 110%;
  color: #ffffff;
`

const Text3 = styled(Typography)`
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  color: #ffffff;
`

const TextNotice = styled(Typography)`
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0px;
  text-align: center;
  margin-top: 10px !important;
`

const Brc20Tag = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2px 8px;
  border: 1px solid rgba(191, 255, 11, 0.2);
`

interface MainButtonProps {
  withdrawAmount: string
  receiverAddress: string
  tickerErcName: string
  tokenName: SupportedTicker
  brc20Balance?: BigNumber
}

const FeeDialog = create(
  ({ withdrawAmount, receiverAddress, tokenName, brc20Balance, tickerErcName }: MainButtonProps) => {
    const modal = useModal()

    const { chain } = useNetwork()

    const fee = useFee(tokenName)

    return (
      <Dialog {...muiDialogV5(modal)}>
        <Stack spacing={30} width={{ xs: '100%', md: 400 }}>
          <TextTitle fontSize={{ xs: 32, md: 40 }} sx={{ mt: 20 }}>
            Confirm Withdraw
          </TextTitle>
          <Stack direction="column" spacing={20} sx={{ justifyContent: 'space-between' }}>
            <NetworkSelect tag="To" value={chain?.id} readOnly />
            <BtcSelect tag="From" />
          </Stack>
          <Stack direction="column" sx={{ justifyContent: 'space-between', mt: 20 }} spacing={15}>
            <Stack direction="row" sx={{ justifyContent: 'space-between' }} alignItems={'center'}>
              <Text1 sx={{ opacity: 0.7 }} flex={1}>
                Withdraw amount
              </Text1>
              <Stack direction="row" sx={{ justifyContent: 'space-between' }} alignItems={'center'} spacing={8}>
                <Text1 fontSize={{ xs: 14, md: 20 }} sx={{ wordBreak: 'break-all', textAlign: 'right' }}>
                  {commify(withdrawAmount) || 0} {TICKERS_MAP_BY_NAME[tokenName].name}
                </Text1>
                <Brc20Tag>
                  <Text3 sx={{ color: 'rgba(191, 255, 11, 1)', fontSize: 12, fontWeight: 500 }}>ERC 20</Text3>
                </Brc20Tag>
              </Stack>
            </Stack>
            <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
              <Stack direction="row" alignItems="center" spacing={4}>
                <Text1 sx={{ opacity: 0.7 }} flex={1}>
                  Service fees
                </Text1>
                <Tooltip
                  placement="top"
                  title="We will deduct the corresponding handling fee as the gas fee for your transfer"
                >
                  <ErrorOutlineIcon sx={{ width: 16, height: 16, opacity: 0.5 }} />
                </Tooltip>
              </Stack>
              <Text1 fontSize={{ xs: 14, md: 20 }}>
                {fee ? formatEther(fee?.toString()) : '-'}
                &nbsp;
                {CHAIN_INFO[chain?.id]?.nativeCurrency.symbol}
              </Text1>
            </Stack>
            <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
              <Text1 sx={{ opacity: 0.7 }} flex={1}>
                You will receive
              </Text1>

              <Stack direction="row" sx={{ justifyContent: 'space-between' }} alignItems={'center'} spacing={8}>
                <Text1 fontSize={{ xs: 14, md: 20 }} sx={{ wordBreak: 'break-all', textAlign: 'right' }} flex={1}>
                  {commify(withdrawAmount) || 0} {TICKERS_MAP_BY_NAME[tokenName].name}
                </Text1>
                <Brc20Tag>
                  <Text3 sx={{ color: 'rgba(191, 255, 11, 1)', fontSize: 12 }}>BRC 20</Text3>
                </Brc20Tag>
              </Stack>
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} sx={{ justifyContent: 'space-between' }} spacing={5}>
              <Text1 sx={{ opacity: 0.7 }}>Recipient Address</Text1>
              <Text1 fontWeight={400} sx={{ width: { xs: '100%', md: 237 }, wordBreak: 'break-all' }}>
                {receiverAddress}
              </Text1>
            </Stack>
          </Stack>
          <FeeButton
            withdrawAmount={withdrawAmount}
            receiverAddress={receiverAddress}
            tickerErcName={tickerErcName}
            tokenName={tokenName}
            brc20Balance={brc20Balance}
          />
          <TextNotice sx={{ color: '#FFFFFFE5' }}>Estimated 8 hours to confirm</TextNotice>
        </Stack>
      </Dialog>
    )
  },
)
register(FeeModalKey, FeeDialog)
export default FeeDialog
